import React, { useState, useEffect, useRef } from "react";
import { doc, setDoc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { auth, db } from "../firebase";

import WelcomeScreen from "./WelcomeScreen";
import AssessmentFlowPage from "./AssessmentFlowPage";
import SignUpTeaser from "./SignUpTeaser";
import AuthScreen from "./AuthScreen";
import SolutionDashboard from "./SolutionDashboard";
import { mapAnswersToProblems } from "../utils/assessmentUtils";
import { signOut } from "firebase/auth";

const BADGE_MILESTONES = [
  { days: 3, name: "Sunbeam Starter" },
  { days: 7, name: "Golden Glow" },
  { days: 14, name: "Radiant Ripple" },
  { days: 21, name: "Sunny Spirit" },
  { days: 30, name: "Sunshine Star" },
  { days: 60, name: "Solar Flare" },
  { days: 100, name: "Sun Guardian" }
];

function getLocalDateString(dateObj) {
  // Returns YYYY-MM-DD in local time
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default function MainFlow({ user }) {
  const [stage, setStage] = useState("welcome");
  const [lockedProblems, setLockedProblems] = useState([]);
  const [lockStartDate, setLockStartDate] = useState(null);
  const [dailyLogs, setDailyLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Gamification & Profile states
  const [points, setPoints] = useState(0);
  const [streak, setStreak] = useState(0);
  const [lastCheckIn, setLastCheckIn] = useState(null);
  const [badges, setBadges] = useState([]);
  const [skills, setSkills] = useState({ stress: 0, anxiety: 0, sunlight: 0 });
  const [displayName, setDisplayName] = useState("Anonymous");
  const [avatar, setAvatar] = useState("🙂");

  // New badge popup state
  const [newBadge, setNewBadge] = useState(null);

  // --- AUTO LOGOUT LOGIC ---
  const logoutTimer = useRef();

  useEffect(() => {
    if (!user) return;

    const resetTimer = () => {
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
      logoutTimer.current = setTimeout(() => {
        handleLogout();
        alert("You have been logged out due to inactivity.");
      }, 1000 * 60 * 30); // 30 minutes
    };

    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("keydown", resetTimer);
    resetTimer();

    return () => {
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("keydown", resetTimer);
    };
    // eslint-disable-next-line
  }, [user]);
  // --- END AUTO LOGOUT LOGIC ---

  useEffect(() => {
    if (user) {
      goToDashboard(user);
    } else {
      resetAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  function resetAll() {
    setStage("welcome");
    setLockedProblems([]);
    setLockStartDate(null);
    setDailyLogs([]);
    setPoints(0);
    setStreak(0);
    setLastCheckIn(null);
    setBadges([]);
    setSkills({ stress: 0, anxiety: 0, sunlight: 0 });
    setDisplayName("Anonymous");
    setAvatar("🙂");
    setLoading(false);
    setNewBadge(null);
  }

  const goToDashboard = async (fbUser = user) => {
    setLoading(true);
    try {
      const docRef = doc(db, "userProblems", fbUser.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setLockedProblems(data.lockedProblems || []);
        setLockStartDate(data.lockStartDate || null);
        setDailyLogs(data.dailyLogs || []);
        setPoints(data.points || 0);
        setStreak(data.streak || 0);
        setLastCheckIn(data.lastCheckIn || null);
        setBadges(Array.isArray(data.badges) ? data.badges : []);
        setSkills(data.skills || { stress: 0, anxiety: 0, sunlight: 0 });
        setDisplayName(data.displayName || "Anonymous");
        setAvatar(data.avatar || "🙂");
      } else {
        resetAll();
      }
    } catch (err) {
      console.error("Failed to fetch user data:", err);
      resetAll();
    }
    setLoading(false);
    setStage("dashboard");
  };

  // Points, streak, and skills are incremented here
  const handleDailyCheckIn = async (log) => {
    if (!user) return;

    // Get today's date in local time (YYYY-MM-DD)
    const todayDateObj = new Date();
    todayDateObj.setHours(0, 0, 0, 0);
    const today = getLocalDateString(todayDateObj);

    // Defensive: badges is always an array
    const safeBadges = Array.isArray(badges) ? badges : [];

    // Streak logic (local time, midnight)
    let newStreak = streak;
    if (lastCheckIn) {
      const lastDateObj = new Date(lastCheckIn);
      lastDateObj.setHours(0, 0, 0, 0);
      const diffDays = Math.floor((todayDateObj - lastDateObj) / (1000 * 60 * 60 * 24));
      // Debug logging
      console.log("lastCheckIn:", lastCheckIn, "today:", today, "diffDays:", diffDays);

      if (diffDays === 1) {
        newStreak = streak + 1;
      } else if (diffDays > 1) {
        newStreak = 1;
      }
      // If diffDays === 0, do not increment streak
    } else {
      newStreak = 1;
    }

    // Badge logic
    let newBadges = [...safeBadges];
    let earnedBadges = [];
    BADGE_MILESTONES.forEach(milestone => {
      if (newStreak === milestone.days && !safeBadges.includes(milestone.name)) {
        newBadges.push(milestone.name);
        earnedBadges.push(milestone.name);
      }
    });

    if (earnedBadges.length > 0) {
      setNewBadge(earnedBadges[earnedBadges.length - 1]);
    }

    const logWithDate = { ...log, date: today };

    const newPoints = points + 10;
    const newSkills = { ...skills };
    // Only increment skill if user actually practiced
    if (log.type && newSkills.hasOwnProperty(log.type) && log.didPractice) {
      newSkills[log.type] += 1;
    }

    await updateDoc(doc(db, "userProblems", user.uid), {
      dailyLogs: arrayUnion(logWithDate),
      points: newPoints,
      streak: newStreak,
      lastCheckIn: today,
      badges: newBadges,
      skills: newSkills,
    });

    setDailyLogs((prev) => [...prev, logWithDate]);
    setPoints(newPoints);
    setStreak(newStreak);
    setLastCheckIn(today);
    setBadges(newBadges);
    setSkills(newSkills);
  };

  // Profile display name update
  const handleSetDisplayName = async (newName) => {
    if (!user) return;
    await updateDoc(doc(db, "userProblems", user.uid), {
      displayName: newName,
    });
    setDisplayName(newName);
  };

  // Avatar update
  const handleSetAvatar = async (newAvatar) => {
    if (!user) return;
    await updateDoc(doc(db, "userProblems", user.uid), {
      avatar: newAvatar,
    });
    setAvatar(newAvatar);
  };

  const handleLogout = async () => {
    await signOut(auth);
    resetAll();
  };

  const canRetake =
    lockStartDate &&
    (new Date() - new Date(lockStartDate)) / (1000 * 60 * 60 * 24) >= 21;

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "40vh" }}>
        <p>Loading your data...</p>
        <div style={{ fontSize: "2rem" }}>⏳</div>
      </div>
    );
  }

  let content;

  switch (stage) {
    case "welcome":
      content = (
        <WelcomeScreen
          onStartAssessment={() => setStage("assessment")}
          onLogin={() => setStage("auth-login")}
          onGoToDashboard={() => goToDashboard(user)}
          user={user}
        />
      );
      break;

    case "assessment":
      content = (
        <AssessmentFlowPage
          onComplete={async (answers) => {
            const mappedProblems = mapAnswersToProblems(answers);
            setLockedProblems(mappedProblems);
            setStage("preparing");

            setTimeout(async () => {
              if (user) {
                const now = new Date().toISOString();
                await setDoc(doc(db, "userProblems", user.uid), {
                  lockedProblems: mappedProblems,
                  lockStartDate: now,
                  dailyLogs: [],
                  points: 0,
                  streak: 0,
                  lastCheckIn: null,
                  badges: [],
                  skills: { stress: 0, anxiety: 0, sunlight: 0 },
                  displayName,
                  avatar,
                });
                setLockStartDate(now);
                setDailyLogs([]);
                setPoints(0);
                setStreak(0);
                setLastCheckIn(null);
                setBadges([]);
                setSkills({ stress: 0, anxiety: 0, sunlight: 0 });
                await goToDashboard(user);
              } else {
                setStage("signupTeaser");
              }
            }, 1800);
          }}
        />
      );
      break;

    case "preparing":
      content = (
        <div style={{
          maxWidth: 500,
          margin: "48px auto",
          textAlign: "center",
          background: "#fff",
          borderRadius: 24,
          boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
          padding: 48
        }}>
          <h2>Great, thanks for answering!</h2>
          <p>We're analyzing your answers and creating your personalized program...</p>
          <div style={{ fontSize: 40, margin: "32px 0" }}>⏳</div>
        </div>
      );
      break;

    case "signupTeaser":
      content = (
        <SignUpTeaser
          insights={lockedProblems}
          onSignUp={() => setStage("auth-signup")}
        />
      );
      break;

    case "auth-signup":
      content = (
        <AuthScreen
          mode="signup"
          onAuthSuccess={async () => {
            if (auth.currentUser) {
              const now = new Date().toISOString();
              await setDoc(doc(db, "userProblems", auth.currentUser.uid), {
                lockedProblems,
                lockStartDate: now,
                dailyLogs: [],
                points: 0,
                streak: 0,
                lastCheckIn: null,
                badges: [],
                skills: { stress: 0, anxiety: 0, sunlight: 0 },
                displayName,
                avatar,
              });
              setLockStartDate(now);
              setDailyLogs([]);
              setPoints(0);
              setStreak(0);
              setLastCheckIn(null);
              setBadges([]);
              setSkills({ stress: 0, anxiety: 0, sunlight: 0 });
            }
            await goToDashboard(auth.currentUser);
          }}
        />
      );
      break;

    case "auth-login":
      content = (
        <AuthScreen
          mode="login"
          onAuthSuccess={() => goToDashboard(auth.currentUser)}
        />
      );
      break;

    case "dashboard":
      content =
        !lockedProblems || lockedProblems.length === 0 ? (
          <div style={{ maxWidth: 500, margin: "48px auto", textAlign: "center" }}>
            <h2>Welcome!</h2>
            <p>
              You haven’t taken the assessment yet.<br />
              Start now to get your personalized recommendations.
            </p>
            <button
              style={{
                marginTop: 24,
                padding: "12px 32px",
                borderRadius: 24,
                background: "#FFD166",
                color: "#222",
                fontWeight: 600,
                border: "none",
                cursor: "pointer"
              }}
              onClick={() => setStage("assessment")}
            >
              Start Assessment
            </button>
            {user && (
              <button
                style={{
                  marginTop: 24,
                  padding: "12px 32px",
                  borderRadius: 24,
                  background: "#ef476f",
                  color: "#fff",
                  fontWeight: 600,
                  border: "none",
                  cursor: "pointer"
                }}
                onClick={handleLogout}
              >
                Log Out
              </button>
            )}
          </div>
        ) : (
          <SolutionDashboard
            problems={lockedProblems}
            user={user}
            canRetake={canRetake}
            lockStartDate={lockStartDate}
            dailyLogs={dailyLogs}
            onDailyCheckIn={handleDailyCheckIn}
            onBackToProblems={() => setStage("assessment")}
            onLogout={handleLogout}
            points={points}
            streak={streak}
            badges={badges}
            skills={skills}
            displayName={displayName}
            avatar={avatar}
            onSetDisplayName={handleSetDisplayName}
            onSetAvatar={handleSetAvatar}
            newBadge={newBadge}
            onCloseBadgePopup={() => setNewBadge(null)}
          />
        );
      break;

    default:
      content = null;
  }

  return <>{content}</>;
}
